import type { ActiveEvent } from '../types/dynamicEvents';
import type { GameDate } from '../types/events';
import { DEVELOPMENT_CONSTANTS, GAME_CONSTANTS } from '../config/gameConstants';
import { GAME_FORMULAS } from '../config/gameFormulas';

type PhaseEventProduct = {
  status?: string;
  developmentPhase?: {
    phase?: string;
  };
};

export const isStalePhaseEvent = (
  pendingEvent: ActiveEvent,
  product?: PhaseEventProduct
): boolean => {
  if (!pendingEvent.productContext) {
    return false;
  }

  if (!product) {
    return true;
  }

  const expectedPhase = pendingEvent.productContext.phase;
  const statusPhase = product.status;
  const developmentPhase = product.developmentPhase?.phase;

  if (statusPhase !== expectedPhase) {
    return true;
  }

  if (!developmentPhase || developmentPhase !== expectedPhase) {
    return true;
  }

  return false;
};

export const getDiminishedQualityBonus = (
  baseBonus: number,
  investmentCount: number,
  maxSteps: number = DEVELOPMENT_CONSTANTS.investmentQualityDiminishingSteps
): number => {
  if (!baseBonus || baseBonus <= 0) {
    return 0;
  }

  const clampedMaxSteps = maxSteps > 0 ? maxSteps : 1;
  const effectiveCount = Math.max(0, Math.min(investmentCount, clampedMaxSteps));
  const multiplier = (clampedMaxSteps - effectiveCount) / clampedMaxSteps;

  return Math.max(0, baseBonus * multiplier);
};

type InvestmentPhaseInput = {
  currentPhase: {
    startDate: GameDate;
    estimatedDuration: number;
    progress?: number;
    daysElapsed?: number;
  };
  additionalDays?: number;
  currentDate: GameDate;
};

export const getInvestmentPhaseUpdate = ({
  currentPhase,
  additionalDays,
  currentDate
}: InvestmentPhaseInput): {
  estimatedDuration: number;
  progress: number;
  startDate: GameDate;
  daysElapsed: number;
} => {
  const extraDays = additionalDays || 0;
  const newEstimatedDuration = currentPhase.estimatedDuration + extraDays;
  const rawDaysElapsed = currentPhase.daysElapsed ??
    Math.floor(((currentPhase.progress ?? 0) / 100) * currentPhase.estimatedDuration);
  const maxElapsed = Math.max(0, newEstimatedDuration - 1);
  const clampedDaysElapsed = Math.min(Math.max(0, rawDaysElapsed), maxElapsed);
  const newProgress = Math.min(99, (clampedDaysElapsed / newEstimatedDuration) * 100);
  const newStartDate = getStartDateFromElapsedDays(currentDate, clampedDaysElapsed);

  return {
    estimatedDuration: newEstimatedDuration,
    progress: newProgress,
    startDate: newStartDate,
    daysElapsed: clampedDaysElapsed
  };
};

export const getStartDateFromElapsedDays = (
  currentDate: GameDate,
  daysElapsed: number
): GameDate => {
  const baseYear = GAME_CONSTANTS.time.startingDate.year;
  const totalDays = GAME_FORMULAS.time.gameDateToDays(currentDate, baseYear);
  const adjustedDays = Math.max(0, totalDays - Math.max(0, Math.floor(daysElapsed)));
  const yearsOffset = Math.floor(adjustedDays / GAME_CONSTANTS.time.daysPerYear);
  const dayOfYear = adjustedDays % GAME_CONSTANTS.time.daysPerYear;
  const month = Math.floor(dayOfYear / GAME_CONSTANTS.time.daysPerMonth) + 1;
  const day = (dayOfYear % GAME_CONSTANTS.time.daysPerMonth) + 1;

  return {
    year: baseYear + yearsOffset,
    month,
    day
  };
};

export const getEffectiveGameDate = (
  requestedDate: GameDate | undefined,
  currentDate: GameDate
): GameDate => {
  if (!requestedDate) {
    return currentDate;
  }

  const currentDays = GAME_FORMULAS.time.gameDateToDays(currentDate);
  const requestedDays = GAME_FORMULAS.time.gameDateToDays(requestedDate);

  return requestedDays > currentDays ? requestedDate : currentDate;
};
